import React from 'react';
import { cn } from '../../../lib/utils';

const formatBytes = (bytes, decimals = 2) => {
  if (!bytes || isNaN(bytes) || +bytes === 0) return 'N/A';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    return dateStr;
  }
};

const InfoTab = ({ dataset, isFullScreen = false }) => {
  if (!dataset) return null;

  const isBQ = dataset.source === 'bigquery';
  const stats = dataset.stats || {};

  const infoItems = [
    {
      label: 'Data Source',
      value: isBQ ? 'Google BigQuery' : (dataset.source === 'upload' ? 'Manteo Warehouse' : dataset.source)
    },
    ...(isBQ ? [
      { label: 'GCP Project', value: stats.project || 'N/A' },
      { label: 'Cloud Dataset', value: stats.dataset || 'N/A' },
      { label: 'Storage Type', value: stats.table_type || 'TABLE' },
    ] : []),
    {
      label: 'Added On',
      value: formatDate(dataset.created_at)
    },
    {
      label: 'Record Count',
      value: stats.num_rows !== undefined ? Number(stats.num_rows).toLocaleString() : (dataset.rows ? Number(dataset.rows).toLocaleString() : 'N/A')
    },
    {
      label: 'Total Volume',
      value: stats.num_bytes !== undefined ? formatBytes(stats.num_bytes) : (dataset.size || 'N/A')
    },
  ];

  return (
    <div className="h-full overflow-y-auto custom-scrollbar">
      <table className="w-full text-left border-collapse">
        <tbody className="divide-y divide-gray-50 dark:divide-gray-900/50">
          {infoItems.map((item) => (
            <tr key={item.label} className="group hover:bg-gray-50/50 dark:hover:bg-gray-900/50 transition-all">
              <td className={cn(
                "shrink-0",
                isFullScreen ? "py-5 px-8 w-60" : "py-3 px-6 w-40"
              )}>
                <span className={cn(
                  "font-black text-gray-400 uppercase tracking-widest",
                  isFullScreen ? "text-[10px]" : "text-[10px]"
                )}>{item.label}</span>
              </td>
              <td className={cn(isFullScreen ? "py-5 px-8" : "py-3 px-6")}>
                <span className={cn(
                  "font-bold text-gray-900 dark:text-white uppercase tracking-tight",
                  isFullScreen ? "text-sm" : "text-xs",
                  item.isMono && "font-mono normal-case break-all text-emerald-600 dark:text-emerald-400",
                  item.isMono && (isFullScreen ? "text-xs" : "text-[11px]"),
                  item.isStatus && (dataset.status === 'online' ? "text-emerald-600" : "text-amber-600")
                )}>
                  {item.value}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {isFullScreen && (
        <div className="border-t border-gray-100 dark:border-gray-800 p-8 bg-gray-50/10 dark:bg-gray-900/5">
          <h4 className="text-[11px] font-black text-emerald-600 dark:text-emerald-400 uppercase tracking-[0.3em] mb-6">Column Analysis</h4>

          <div className="bg-white dark:bg-gray-950 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-sm">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-gray-50 dark:border-gray-900 shadow-sm bg-gray-50/30 dark:bg-gray-900/20">
                  <th className="px-6 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Column</th>
                  <th className="px-6 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Type</th>
                  <th className="px-6 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Unique</th>
                  <th className="px-6 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest text-right">Nulls %</th>
                  <th className="px-6 py-3 text-[10px] font-black text-gray-400 uppercase tracking-widest">Metrics</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-900/50">
                {(() => {
                  // Map schema for easy type lookup
                  const schemaMap = (dataset.schema || []).reduce((acc, col) => {
                    acc[col.name] = col.type;
                    return acc;
                  }, {});

                  return Object.entries(dataset.statistics?.statistics || {}).map(([colName, colStat]) => {
                    const totalRowsCount = dataset.statistics?.num_rows || 0;
                    const nullPct = colStat && totalRowsCount > 0
                      ? (colStat.null_count / totalRowsCount * 100).toFixed(1)
                      : null;
                    const colType = schemaMap[colName] || '—';

                    return (
                      <tr key={colName} className="group hover:bg-gray-50/50 dark:hover:bg-gray-900/30 transition-all">
                        <td className="px-6 py-4">
                          <span className="text-xs font-bold text-gray-900 dark:text-white font-mono break-all">{colName}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-[10px] font-black text-emerald-600 dark:text-emerald-500 bg-emerald-50 dark:bg-emerald-900/10 px-2 py-0.5 rounded-lg border border-emerald-100/50 dark:border-emerald-900/30 uppercase">
                            {colType}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs font-bold text-gray-700 dark:text-gray-300">
                            {colStat.distinct_count?.toLocaleString() || '—'}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          {nullPct !== null && parseFloat(nullPct) > 0 ? (
                            <span className="text-[10px] font-black text-amber-600 dark:text-amber-500 bg-amber-50 dark:bg-amber-900/10 px-2 py-0.5 rounded-lg border border-amber-100/50 dark:border-amber-900/30">
                              {nullPct}%
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold text-gray-300">0%</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {(() => {
                            const hasRange = colStat.min !== undefined && colStat.max !== undefined;
                            const hasAvg = colStat.avg !== undefined;
                            const metricsCount = (hasRange ? 2 : 0) + (hasAvg ? 1 : 0);

                            const Label = ({ children, className = "" }) => (
                              <span className={`text-[9px] font-black text-gray-400 uppercase tracking-tighter w-12 shrink-0 ${className}`}>
                                {children}
                              </span>
                            );

                            if (metricsCount > 2) {
                              // Structured table layout for multiple metrics
                              return (
                                <div className="flex flex-col gap-px bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-100 dark:border-gray-800">
                                  <div className="flex bg-white dark:bg-gray-950 px-3 py-1.5 gap-3 items-center">
                                    <Label>Range</Label>
                                    <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300 truncate max-w-[180px]">
                                      {colStat.min} <span className="text-gray-400 font-normal mx-1">→</span> {colStat.max}
                                    </span>
                                  </div>
                                  <div className="flex bg-gray-50/50 dark:bg-gray-900/30 px-3 py-1.5 gap-3 items-center border-t border-gray-50 dark:border-gray-900/50">
                                    <Label>Average</Label>
                                    <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-500">
                                      {typeof colStat.avg === 'number' ? colStat.avg.toLocaleString(undefined, { maximumFractionDigits: 2 }) : colStat.avg}
                                    </span>
                                  </div>
                                </div>
                              );
                            } else if (metricsCount > 0) {
                              // Simple layout for single metric type - indent to match box interior
                              return (
                                <div className="flex items-center gap-3 px-4">
                                  {hasRange ? (
                                    <>
                                      <Label>Range</Label>
                                      <span className="text-[10px] font-bold text-gray-700 dark:text-gray-300">
                                        {colStat.min} <span className="text-gray-400 font-normal mx-0.5">→</span> {colStat.max}
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <Label>Avg</Label>
                                      <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-500">
                                        {typeof colStat.avg === 'number' ? colStat.avg.toLocaleString(undefined, { maximumFractionDigits: 2 }) : colStat.avg}
                                      </span>
                                    </>
                                  )}
                                </div>
                              );
                            } else {
                              // Align N/A with text content as well
                              return <span className="text-[10px] font-bold text-gray-300 uppercase tracking-widest px-4">N/A</span>;
                            }
                          })()}
                        </td>
                      </tr>
                    );
                  });
                })()}
                {Object.keys(dataset.statistics?.statistics || {}).length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center">
                      <span className="text-xs font-black text-gray-400 uppercase tracking-widest italic opacity-50">
                        Compute statistics to see column analysis
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default InfoTab;
